import React from "react";
import { Box, Table, Text, Tag } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import WaitingTimePerServiceLineGraph from "../charts/WaitingTimePerServiceLineGraph";
import ReportDataTable from "../../reports/ReportDataTable";
import {
  WAITING_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  normalizeRenderConfig,
} from "../../../store/renderConfig";
import { formatTime, formatNumber } from "../../../store/helpers";
import { getWaitingTimePerServiceRowsForScope } from "../../../store/waitingTimePerServiceExport";

export const renderWaitingTimePerServiceCell = (serviceData, columnId) => {
  if (columnId === "serviceName") return serviceData.serviceName || serviceData.name;
  if (columnId === "completed") return <Text color="var(--blue)">{formatNumber(serviceData.completed)}</Text>;
  if (columnId === "totalWaitingTime") return <Text color="var(--blue)">{formatTime(serviceData.waitingTimeInSeconds)}</Text>;
  if (columnId === "average") return formatTime(serviceData.avg);
  if (columnId === "min") return formatTime(serviceData.min);
  if (columnId === "max") return formatTime(serviceData.max);
  return null;
};

const WaitingTimePerServiceTable = ({
  branchId = null,
  data,
  branchIndex,
  numServices,
  branchName,
  renderConfig,
  onRenderConfigChange,
}) => {
  const { showEmptyDataSets } = useSelector((state) => state.defaultReducer);
  const config = normalizeRenderConfig(renderConfig, {
    columnDefinitions: WAITING_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  });
  const chartTitle = branchId ? `${branchName} Branch, ` : "Entire Organization,";
  const branchTitle = branchId
    ? `${branchIndex ? `${branchIndex}. ` : ""}${branchName} Branch`
    : "Entire Organization";
  const scope = branchId ? "Branch" : "Organization";
  const rows = getWaitingTimePerServiceRowsForScope({ data, branchName, scope }, config);
  const visibleChartRows = (data || []).filter(
    (item) => showEmptyDataSets || (!showEmptyDataSets && Number(item.completed) > 0)
  );

  return (
    <Box mt={10}>
      <Box mb="20px">
        <h2>{branchId ? branchTitle : "1. Entire Organization"}</h2>
        {branchId ? <p>{numServices} services.</p> : null}
      </Box>

      {!config.charts.enabled ? (
        <Box className="waitingTimePerServiceBlock">
          <ReportDataTable
            columns={WAITING_TIME_PER_SERVICE_COLUMN_DEFINITIONS}
            rows={rows}
            renderConfig={config}
            renderCell={renderWaitingTimePerServiceCell}
            presortedRows={true}
            onSortChange={(sort) => onRenderConfigChange?.({ ...config, sort })}
          />
        </Box>
      ) : null}

      {config.charts.enabled
        ? visibleChartRows.map((serviceData, index) => (
            <Box key={index} className="reportContainer waitingTimePerServiceBlock" mt="20px">
              <Box mb="10px">
                <h3>
                  {branchIndex ? `${branchIndex}. ` : null}
                  {index + 1}. {serviceData.name}
                </h3>
                {branchId ? (
                  <Tag.Root>
                    <Tag.Label className="branchTag">{branchName} Branch</Tag.Label>
                  </Tag.Root>
                ) : null}
              </Box>

              <Table.Root className="stripedTable" size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader borderRight="1px solid #dadce0">Total Completed Tickets</Table.ColumnHeader>
                    <Table.ColumnHeader borderRight="1px solid #dadce0">Total Waiting Time</Table.ColumnHeader>
                    <Table.ColumnHeader>Avg /ticket.</Table.ColumnHeader>
                    <Table.ColumnHeader>Min.</Table.ColumnHeader>
                    <Table.ColumnHeader>Max</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell borderRight="1px solid #dadce0">{renderWaitingTimePerServiceCell(serviceData, "completed")}</Table.Cell>
                    <Table.Cell borderRight="1px solid #dadce0">{renderWaitingTimePerServiceCell(serviceData, "totalWaitingTime")}</Table.Cell>
                    <Table.Cell>{renderWaitingTimePerServiceCell(serviceData, "average")}</Table.Cell>
                    <Table.Cell>{renderWaitingTimePerServiceCell(serviceData, "min")}</Table.Cell>
                    <Table.Cell>{renderWaitingTimePerServiceCell(serviceData, "max")}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell colSpan={5} p={0}>
                      <Box mt="20px" mb="20px">
                        <WaitingTimePerServiceLineGraph
                          title={`${serviceData.name}: ${chartTitle} Day by Day Comparison`}
                          chart={serviceData.days}
                          chartHeight="200px"
                        />
                      </Box>
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table.Root>
            </Box>
          ))
        : null}
      {!rows.length && !config.charts.enabled ? (
        <Text fontSize="sm">No data available for this report</Text>
      ) : null}
    </Box>
  );
};

export default WaitingTimePerServiceTable;
